import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";

import { ViewState } from "react-map-gl/mapbox";
import { memo } from "react";
import { PaymentHistoryModalProps } from "@/modules/finance/helper/types/payment-history-modal.type";
import { moneyFormat } from "@/helper/format-data";
import { defaultDurationOptions } from "@/helper/date/constants/duration.const";
import { toLocalString } from "@/helper/date/to-local-string.util";
import { formatDuration } from "@/helper/date/format-duration.util";
import { numberToDateTimeString } from "@/helper/date/number-to-date-time.util";
import { ZoneNameBadge } from "./columns/ZoneNameBadge";
import { StatusBadge } from "./columns/StatusBadge";
import { UserProfileBadge } from "./columns/UserProfileBadge";
import { TransactionState } from "./columns/TransactionState";
import { PaymentMethodLogo } from "./columns/PaymentMethodLogo";
import { INITIAL_VIEW_STATE, mapboxStyle } from "@/constants/map.const";
import { CustomMap } from "@/components/map/custom-map/CustomMap";
import { AddressMarker } from "@/components/map/address-marker/AddressMarker";

export const TransactionDetailModal = ({ onClose, paymentHistory }: PaymentHistoryModalProps) => {
  const startDateTime = toLocalString({
    date: paymentHistory.start_date,
    locale: paymentHistory.country_code,
    timeZone: paymentHistory.iana,
  });

  const endDateTime = toLocalString({
    date: paymentHistory.end_date,
    locale: paymentHistory.country_code,
    timeZone: paymentHistory.iana,
  });

  const duration = formatDuration(numberToDateTimeString(paymentHistory.duration), {
    ...defaultDurationOptions,
    delimiter: " ",
    zero: false,
  });

  const amount = paymentHistory.amount ? moneyFormat(paymentHistory.amount / 100) : "Gratuit";

  const address = paymentHistory.address_details
    ? `${paymentHistory.address_details.street_number} ${paymentHistory.address_details.street_name}`
    : "";
  const city = paymentHistory.address_details
    ? `${paymentHistory.address_details.postal_code} ${paymentHistory.address_details.city}`
    : "";

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent showCloseButton={true} className="overflow-hidden p-4 w-[852px] sm:max-w-[852px] rounded-[10px]">
        <DialogTitle className="sr-only">Détails de la transaction</DialogTitle>
        <DialogDescription className="sr-only">{paymentHistory.id}</DialogDescription>
        <div className="flex">
          <div className="w-[280px] h-[342px] flex-shrink-0 rounded-lg overflow-hidden" id="transaction-map">
            {paymentHistory.location && (
              <LocationMap
                position={{
                  latitude: paymentHistory.location?.coordinates[1],
                  longitude: paymentHistory.location?.coordinates[0],
                  zoom: 18,
                }}
              />
            )}
          </div>

          <div className="flex-1 p-[23px] pr-[13px] divide-y">
            <div className="grid grid-cols-3 pb-4 gap-2">
              <div className="flex flex-col">
                <div className="text-xs leading-[21px] font-semibold text-table-header">Date de début</div>
                <div className="font-black text-sm leading-4">{startDateTime.date}</div>
                <div className="font-extrabold text-xs leading-2.5 text-trend-neutral">{startDateTime.time}</div>
              </div>

              <div className="flex flex-col">
                <div className="text-xs leading-[21px] font-semibold text-table-header">Date de fin</div>
                <div className="font-black text-sm leading-4">{endDateTime.date}</div>
                <div className="font-extrabold text-xs leading-2.5 text-trend-neutral">{endDateTime.time}</div>
              </div>
              <div className="flex flex-col">
                <div className="text-xs leading-[21px] font-semibold text-table-header">Durée</div>
                <div className="font-black text-sm leading-4">{duration}</div>
              </div>
            </div>

            <div className="grid grid-cols-3 py-4 gap-2">
              <div className="flex flex-col">
                <div className="text-xs leading-[21px] font-semibold text-table-header">Immatriculation</div>
                <div className="font-black text-sm leading-4">{paymentHistory.license_plate}</div>
              </div>

              <div className="flex flex-col">
                <div className="text-xs leading-[21px] font-semibold text-table-header">Montant</div>
                <div className="font-black text-sm leading-4">{amount}</div>
              </div>

              <div className="flex flex-col">
                <div className="text-xs leading-[21px] font-semibold text-table-header">Mode de paiement</div>
                <div className="flex items-center">
                  <PaymentMethodLogo
                    paymentMethodWallet={paymentHistory.payment_method.wallet}
                    paymentMethodName={paymentHistory.payment_method.brand}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 py-4 gap-2">
              <div className="flex flex-col">
                <div className="text-xs leading-[21px] font-semibold text-table-header h-[21px] flex flex-col justify-center">
                  Profil usager
                </div>
                <UserProfileBadge userProfile={paymentHistory.user_profile} className="w-20 px-0" />
              </div>

              <div className="flex flex-col">
                <div className="text-xs leading-[21px] font-semibold text-table-header h-[21px] flex flex-col justify-center">
                  Zone
                </div>
                <ZoneNameBadge zoneName={paymentHistory.zone_name} className="w-20 px-0" />
              </div>

              <div className="flex flex-col">
                <div className="text-xs leading-[21px] font-semibold text-table-header">Statut</div>
                <StatusBadge status={paymentHistory.state === "fulfilled"} />
              </div>
            </div>

            <div className="grid grid-cols-3 pt-4 gap-2">
              <div className="flex flex-col">
                <div className="text-xs leading-[21px] font-semibold text-table-header">Arrêté / Prolongé</div>
                <TransactionState
                  stopped={paymentHistory.stopped}
                  extended={paymentHistory.extended}
                  className="text-sm leading-3"
                />
              </div>

              <div className="flex flex-col">
                <div className="text-xs leading-[21px] font-semibold text-table-header">Transaction ID</div>
                <span className="font-black text-sm leading-3 break-all">{paymentHistory.id}</span>
              </div>

              <div className="flex flex-col">
                <div className="text-xs leading-[21px] font-semibold text-table-header">Adresse</div>
                <span className="font-black text-sm leading-3">{address}</span>
                <span className="font-black text-sm leading-3">{city}</span>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

type PositionMapProps = {
  position?: Pick<ViewState, "latitude" | "longitude" | "zoom">;
};

const LocationMap = memo(({ position }: PositionMapProps) => {
  return (
    <CustomMap
      onViewPortChange={() => {}}
      mapStyle={mapboxStyle["normal"]}
      initialView={{ ...INITIAL_VIEW_STATE, ...position }}
      reuseMaps
    >
      {position && <AddressMarker position={[position.longitude, position.latitude]} iconColor="#1B8981" />}
    </CustomMap>
  );
});
